import {
    ComputeStatus,
    useFetchDataSourceConfigLazyQuery,
} from "@biggeo/bg-server-lib/studio";
import React, { useEffect } from "react";
import { SetDatasetContextType } from "../../map/mapbox/context/utils/general-utils";

export type DataSourceConfigStatusWrapperProps = Readonly<
    {
        fkDatasourceId: string;
        fkOrganizationId?: string;
        status: ComputeStatus;
        children: React.FC<
            Readonly<{
                status: ComputeStatus;
            }>
        >;
    } & Pick<SetDatasetContextType, "updateDataset">
>;

/**
 * @description A wrapper that checks the status of the data_source_configuration linked to the fkDatasourceId
 *      and fkOrganizationId every 10 seconds as long as its status is loading.
 * @returns The updated status
 */

export const DataSourceConfigStatusWrapper = ({
    children,
    updateDataset,
    status,
    fkDatasourceId,
    fkOrganizationId,
}: DataSourceConfigStatusWrapperProps) => {
    const {
        executeQuery,
        queryReturn: [_, { data }],
    } = useFetchDataSourceConfigLazyQuery();

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        if (status === ComputeStatus.loading && fkOrganizationId) {
            const interval = setInterval(() => {
                executeQuery({
                    variables: {
                        dataSourceId: fkDatasourceId,
                        organizationId: fkOrganizationId,
                    },
                    onCompleted: (data) => {
                        updateDataset({
                            dataSourceId: fkDatasourceId,
                            dataset: {
                                dataSourceConfiguration:
                                    data.fetchDataSourceConfig,
                            },
                        });

                        // Stop when status changes
                        if (
                            data.fetchDataSourceConfig.status !==
                            ComputeStatus.loading
                        ) {
                            clearInterval(interval);
                        }
                    },
                });
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [status, fkDatasourceId, fkOrganizationId]);

    return children({
        status: data?.fetchDataSourceConfig.status || status,
    });
};
